import { useEffect, useState, useCallback } from "react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, BellRing, MapPin, ShieldCheck, ShieldAlert, Save, Plus } from "lucide-react";

const CAT_LABEL = {
  common_area_cleaning: "Common-area cleaning", trash: "Trash & garbage", landscaping: "Landscaping",
  plumbing: "Plumbing", electrical: "Electrical", hvac: "HVAC", pest_control: "Pest control",
  pool: "Pool", handyman: "Handyman", janitorial: "Janitorial", other: "Other",
};

/* The vendor's own switchboard: what work they want to hear about, and where. Previously the
   endpoint existed but nothing on their home let them turn the noise down. */
export default function VendorAlerts({ token }) {
  const [d, setD] = useState(null);
  const [a, setA] = useState(null);
  const [city, setCity] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/fs/vendor/${token}/paperwork`);
    setD(data);
    setA({ enabled: data.alerts?.enabled !== false, trades: data.alerts?.trades || [], markets: data.alerts?.markets || [] });
  }, [token]);
  useEffect(() => { load().catch(() => { /* home screen still works without it */ }); }, [load]);

  if (!d || !a) return null;

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/fs/vendor/${token}/alerts`, a);
      toast.success(a.enabled ? "Saved — we'll only text you work that matches" : "Alerts off — you can still open work from this page");
      await load();
    } catch (e) { toast.error(errMsg(e, "Couldn't save that")); }
    setBusy(false);
  };

  const toggleTrade = (k) => setA({
    ...a, trades: a.trades.includes(k) ? a.trades.filter((x) => x !== k) : [...a.trades, k],
  });
  const addCity = () => {
    // Commas belong INSIDE a place name ("Round Rock, TX"). Semicolons separate places.
    const parts = city.split(/[;\n]/).map((s) => s.trim()).filter(Boolean);
    if (!parts.length) return;
    setA({ ...a, markets: [...new Set([...a.markets, ...parts])].slice(0, 20) });
    setCity("");
  };

  const paperOk = (d.coi?.status === "verified" || d.coi?.status === "attested")
    && d.w9?.on_file && d.agreement?.signed;

  return (
    <div className="mt-6 rounded-2xl bg-white p-5" data-testid="vendor-alerts">
      <div className="mb-1 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-ink/40">
        <BellRing className="h-3.5 w-3.5" /> What we text you about
      </div>
      <p className="text-sm leading-relaxed text-ink/55">
        We'd rather send you three jobs you want than thirty you don't. Pick the trades and the towns
        you actually cover.
      </p>

      <button data-testid="va-toggle" onClick={() => setA({ ...a, enabled: !a.enabled })}
        className={`mt-3 flex w-full items-center justify-between gap-3 rounded-xl border p-3.5 text-left transition-colors ${a.enabled ? "border-emerald-200 bg-emerald-50" : "border-sand-200 bg-sand-50"}`}>
        <span>
          <span className="block text-sm font-bold text-ink">Tell me when there's work</span>
          <span className="block text-xs text-ink/55">New projects and requests that match, by text and email.</span>
        </span>
        <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold ${a.enabled ? "bg-emerald-600 text-white" : "bg-sand-200 text-ink/60"}`}>
          {a.enabled ? "On" : "Off"}
        </span>
      </button>

      {a.enabled && (
        <div className="mt-4 space-y-4">
          <div>
            <div className="mb-1.5 text-xs font-bold uppercase tracking-wider text-ink/40">Trades you want</div>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(CAT_LABEL).map(([k, l]) => {
                const on = a.trades.includes(k);
                return (
                  <button key={k} data-testid={`va-trade-${k}`} onClick={() => toggleTrade(k)}
                    className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${on ? "bg-redline-500 text-white" : "bg-sand-100 text-ink/60 hover:bg-sand-200"}`}>
                    {l}
                  </button>
                );
              })}
            </div>
            {!a.trades.length && (
              <p className="mt-1.5 text-xs text-amber-700" data-testid="va-no-trades">
                Nothing picked — you'll hear about everything. That's fine, just noisier.
              </p>
            )}
          </div>

          <div>
            <div className="mb-1.5 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-ink/40">
              <MapPin className="h-3 w-3" /> Towns you cover
            </div>
            <div className="flex flex-wrap gap-1.5">
              {a.markets.map((m) => (
                <span key={m} data-testid={`va-market-${m}`}
                  className="inline-flex items-center gap-1.5 rounded-full bg-sand-100 px-3 py-1.5 text-xs font-semibold text-ink/65">
                  {m}
                  <button data-testid={`va-market-rm-${m}`} aria-label={`Remove ${m}`}
                    onClick={() => setA({ ...a, markets: a.markets.filter((x) => x !== m) })}
                    className="text-ink/35 hover:text-redline-600">×</button>
                </span>
              ))}
            </div>
            <div className="mt-2 flex gap-2">
              <input data-testid="va-market-input" value={city} placeholder="Round Rock, TX"
                onChange={(e) => setCity(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCity(); } }}
                className="min-w-0 flex-1 rounded-xl border border-sand-300 px-3 py-2 text-sm outline-none" />
              <button data-testid="va-market-add" onClick={addCity} disabled={!city.trim()}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-xl border border-sand-300 px-3 py-2 text-sm font-bold text-ink/70 disabled:opacity-40">
                <Plus className="h-3.5 w-3.5" /> Add
              </button>
            </div>
            <p className="mt-1 text-xs text-ink/40">One town at a time, or paste a list separated by semicolons.</p>
          </div>
        </div>
      )}

      <button data-testid="va-save" onClick={save} disabled={busy}
        className="mt-4 inline-flex items-center gap-1.5 rounded-xl bg-ink px-4 py-2.5 text-sm font-bold text-white disabled:opacity-40">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
      </button>

      <div className="mt-5 border-t border-sand-200 pt-4" data-testid="va-paperwork">
        <div className="mb-1.5 text-xs font-bold uppercase tracking-wider text-ink/40">
          What you need before you can bid
        </div>
        {paperOk ? (
          <div className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-700">
            <ShieldCheck className="h-3.5 w-3.5" /> All set — you can bid and take jobs
            {d.coi?.expires_on ? ` · insured thru ${d.coi.expires_on}` : ""}
          </div>
        ) : (
          <div className="flex items-start gap-2 rounded-xl bg-amber-50 px-3 py-2.5 text-xs leading-relaxed text-amber-900">
            <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
            <span>
              Still needed: {[
                !(d.coi?.status === "verified" || d.coi?.status === "attested") && "a current certificate of insurance",
                !d.w9?.on_file && "a W-9",
                !d.agreement?.signed && "the signed agreement",
              ].filter(Boolean).join(", ")}. You can still be told about work and come out to look at a job.
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
